import React, { useState, useEffect } from 'react';
import './RatingForm.css';
import '@fortawesome/fontawesome-free/css/all.min.css';


// 🟡 API: Send OTP to email
const sendOtp = async (email) => {
  try {
    const response = await fetch('https://emporio1-1blc.vercel.app/api/sendOTP', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error sending OTP:', error);
    return { success: false, message: 'Failed to send OTP' };
  }
};

// 🟡 API: Verify OTP
const verifyOtp = async (email, otp) => {
  try {
    const response = await fetch('https://emporio1-1blc.vercel.app/api/verifyOTP', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, otp }),
    });
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error verifying OTP:', error);
    return { success: false, message: 'Failed to verify OTP' };
  }
};

// 🟡 Submit rating
const submitRating = async (StoreID, mobile, email, rating, submitted_at, userName) => {
  try {
    const response = await fetch('https://emporio1-1blc.vercel.app/api/submitRating', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        StoreID,
        mobile,
        email,
        rating,
        submitted_at,
        name: userName || null, // Include userName here
      }),
    });
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error submitting rating:', error);
    return { success: false, message: 'Failed to submit rating' };
  }
};

const RatingForm = ({ storeId, onClose }) => {
  const [userName, setUserName] = useState(''); // For user's name
  const [mobile, setMobile] = useState('');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpRequested, setOtpRequested] = useState(false);
  const [isOtpValidated, setIsOtpValidated] = useState(false);
  const [rating, setRating] = useState(null);
  const [errors, setErrors] = useState({});
  const [fieldsLocked, setFieldsLocked] = useState(false);
  const [toasts, setToasts] = useState([]);
  const [otpTimer, setOtpTimer] = useState(0);
  const [resendCount, setResendCount] = useState(0);
  const RESEND_LIMIT = 3;
  const [sendingOtp, setSendingOtp] = useState(false);
  const [validatingOtp, setValidatingOtp] = useState(false);
  const [submittingRating, setSubmittingRating] = useState(false);

  useEffect(() => {
    let timer;
    if (otpTimer > 0) {
      timer = setTimeout(() => setOtpTimer(otpTimer - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [otpTimer]);

const showToast = (message, isError = false) => {
  const id = Date.now(); // more readable than new Date().getTime()
  const newToast = { id, message, isError };

  setToasts((prevToasts) => [newToast, ...prevToasts]);

  setTimeout(() => {
    setToasts((prevToasts) =>
      prevToasts.filter((toast) => toast.id !== id)
    );
  }, 3000); // Auto-remove after 3 seconds
};


  const validateEmailFormat = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  const handleMobileChange = (e) => {
    if (fieldsLocked) return;
    const value = e.target.value.replace(/\D/g, '');
    if (value.length > 10) {
      showToast('Mobile number cannot be more than 10 digits', true);
      return;
    }
    setMobile(value);
  };

  const handleEmailChange = (e) => {
    if (fieldsLocked) return;
    const value = e.target.value;
    setEmail(value);
    if (value && !validateEmailFormat(value)) {
      setErrors((prev) => ({ ...prev, email: 'Invalid email format' }));
    } else {
      setErrors((prev) => ({ ...prev, email: null }));
    }
  };

  const handleUserNameChange = (e) => {
    if (fieldsLocked) return;
    setUserName(e.target.value);
  };

const handleSendOtp = async () => {
  if (!email) {
    showToast('Please enter your email', true);
    return;
  }

  if (!validateEmailFormat(email)) {
    showToast('Please enter a valid email address', true);
    return;
  }

  setSendingOtp(true);

  try {
    // Use the correct API endpoint (should be consistent - either localhost or vercel)
    const response = await fetch('https://emporio1-1blc.vercel.app/api/sendOTP', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email })
    });

    const result = await response.json();

    if (response.ok && result.success) {
      setOtpSent(true);
      setOtpRequested(true);
      setOtpTimer(30); // lock resend for 30 seconds
      setResendCount(prev => prev + 1);
      
      // Show different messages for first send vs resend
      if (resendCount === 0) {
        showToast('OTP sent successfully to your email');
      } else {
        showToast('OTP resent successfully to your email');
      }
    } else {
      // ✅ Only show "User already exists" error for the FIRST attempt, not for resend
      if (result.message?.includes('User already exists') && resendCount === 0) {
        showToast('User already exists. Kindly use another email.', true);
      } else if (result.message?.includes('User already exists') && resendCount > 0) {
        // For resend attempts, ignore the "user exists" error and treat as success
        setOtpSent(true);
        setOtpRequested(true);
        setOtpTimer(30);
        setResendCount(prev => prev + 1);
        showToast('OTP resent successfully to your email');
      } else {
        showToast(result.message || 'Failed to send OTP', true);
      }
    }
  } catch (error) {
    console.error('Error sending OTP:', error);
    showToast('Something went wrong while sending OTP', true);
  } finally {
    setSendingOtp(false);
  }
};

 const handleValidateOtp = async () => {
  if (!otp.trim()) {
    showToast('Please enter the OTP', true);
    return;
  }

  setValidatingOtp(true);
  
  try {
    const result = await verifyOtp(email, otp);
    
    if (result.success) {
      setFieldsLocked(true);
      setIsOtpValidated(true);
      showToast('OTP validated successfully');
    } else {
      showToast(result.message || 'Invalid OTP', true);
    }
  } catch (error) {
    console.error('Error validating OTP:', error);
    showToast('Something went wrong while validating OTP', true);
  } finally {
    setValidatingOtp(false);
  }
};

  const handleAddRating = async () => {
    if (rating === null) {
      showToast('Please select a rating before submitting', true);
      return;
    }

    setSubmittingRating(true);

    try {
      // Use the storeId passed from the parent component
      const submitted_at = new Date().toISOString();
      const result = await submitRating(storeId, mobile, email, rating, submitted_at, userName);

      if (result.message === 'Rating submitted successfully' || result.success) {
        showToast('Rating added successfully');
        setTimeout(() => {
          if (onClose) onClose();
        }, 2000);
      } else {
        showToast(result.message || 'Failed to submit rating', true);
      }
    } catch (error) {
      console.error('Error submitting rating:', error);
      showToast('Something went wrong while submitting rating', true);
    } finally {
      setSubmittingRating(false);
    }
  };

  const handleReloadForm = () => {
    setUserName('');
    setMobile('');
    setEmail('');
    setOtp('');
    setOtpSent(false);
    setOtpRequested(false);
    setIsOtpValidated(false);
    setRating(null);
    setErrors({});
    setFieldsLocked(false);
    setOtpTimer(0);
    setResendCount(0);
    setSendingOtp(false);
    setValidatingOtp(false);
    setSubmittingRating(false);
    showToast('Form reloaded. You can try again.');
  };

  const isResendDisabled = fieldsLocked || otpTimer > 0 || resendCount >= RESEND_LIMIT || sendingOtp;

  return (
    <div className="popup-overlay">
      <div className="popup-container">
        <div className="popup-header">
          <h2>WELCOME TO THE AUTOFORM INDIA</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="toast-container">
          {toasts.map((toast) => (
            <div
              key={toast.id}
              className={`toast ${toast.isError ? 'error-toast' : 'success-toast'}`}
            >
              {toast.message}
            </div>
          ))}
        </div>

        <div className="form-content">

<div className="input-row">
  <div className="input-wrapper input-icon-wrapper">
    <i className="fas fa-user input-icon"></i>
    <input
      type="text"
      placeholder="Enter Name (optional)"
      value={userName}
      onChange={handleUserNameChange}
      disabled={fieldsLocked}
    />
  </div>

  <div className="input-wrapper input-icon-wrapper">
    <i className="fas fa-phone input-icon"></i>
    <input
      type="text"
      placeholder="Enter Mobile no. *"
      value={mobile}
      onChange={handleMobileChange}
      disabled={fieldsLocked}
    />
  </div>

  <div className="input-wrapper input-icon-wrapper">
    <i className="fas fa-envelope input-icon"></i>
    <input
      type="email"
      placeholder="Enter Email*"
      value={email}
      onChange={handleEmailChange}
      disabled={fieldsLocked}
    />
    {errors.email && <span className="error">{errors.email}</span>}
  </div>
</div>

          <div className="validation-heading">
            <h4>Validate Your Email For Giving the ratings*</h4>
          </div>

          <div className="otp-row">
<button
  className={`get-otp-btn ${fieldsLocked ? 'validated' : ''}`}
  onClick={handleSendOtp}
  disabled={isResendDisabled}
>
  {sendingOtp
    ? 'Sending...'
    : fieldsLocked
    ? 'OTP VALIDATED'
    : otpRequested && otpTimer === 0
    ? `RESEND OTP (${resendCount}/${RESEND_LIMIT})`
    : 'GET OTP'}
</button>

            {otpSent && !isOtpValidated && (
              <>
                <div className="otp-input-wrapper">
                  <input
                    type="text"
                    className="otp-input"
                    placeholder="Enter OTP"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    maxLength="6"
                  />
                </div>

               <button className="validate-btn" onClick={handleValidateOtp} disabled={validatingOtp}>
  {validatingOtp ? 'Validating...' : 'Validate'}
</button>

                {otpTimer > 0 && (
                  <span className="timer-text">Wait {otpTimer}s to resend OTP</span>
                )}
              </>
            )}

            {resendCount >= RESEND_LIMIT && !fieldsLocked && (
              <div className="reload-section">
                <button className="reload-btn" onClick={handleReloadForm}>
                  Reload Form
                </button>
              </div>
            )}
          </div>

          {isOtpValidated && (
            <div className="rating-section">
              <h3>ADD RATINGS</h3>
              <div className="rating-container">
                <div className="rating-buttons">
                  {[1, 2, 3, 4, 5].map((num) => (
                    <div className="rating-wrapper" key={num}>
                      <button
                        className={`rating-btn ${rating === num ? 'selected' : ''}`}
                        onClick={() => setRating(num)}
                      >
                        {num}
                      </button>
                    </div>
                  ))}
                </div>
                <button 
                  className="add-btn" 
                  onClick={handleAddRating}
                  disabled={submittingRating}
                >
                  {submittingRating ? 'SUBMITTING...' : 'ADD'}
                </button>
              </div>

              <div className="rating-label-container">
                {rating && (
                  <div className="rating-label">
                    {[
                      'Poor 😢',
                      'Fair 😐',
                      'Good 🙂',
                      'Very Good 😄',
                      'Excellent 😍',
                    ][rating - 1]}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RatingForm;